import React, { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import {
  supportTicketService,
  SupportTicketType,
} from '../services/supportTicketService';
import './SupportTicketModal.css';

export interface SupportTicketProduct {
  id: string;
  name: string;
}

interface SupportTicketModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: SupportTicketType;
  product?: SupportTicketProduct;
}

const SupportTicketModal: React.FC<SupportTicketModalProps> = ({
  isOpen,
  onClose,
  type,
  product,
}) => {
  const { user, userAccountType } = useAuth();
  const [subject, setSubject] = useState(
    type === 'product_flag' && product ? `Flag: ${product.name}` : ''
  );
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  if (!isOpen) return null;

  const title =
    type === 'product_flag'
      ? 'Report a product'
      : type === 'vendor_support'
        ? 'Contact support'
        : 'Contact support';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    try {
      setSubmitting(true);
      setError(null);

      await supportTicketService.createTicket({
        type,
        subject: subject.trim(),
        message: message.trim(),
        reporterId: user.uid,
        reporterEmail: user.email,
        reporterName: user.displayName || user.email,
        reporterType:
          userAccountType === 'vendor'
            ? 'vendor'
            : userAccountType === 'admin'
              ? 'admin'
              : 'customer',
        productId: product?.id,
        productName: product?.name,
        priority: type === 'product_flag' ? 'high' : 'normal',
      });

      setSuccess(true);
      setMessage('');
      if (type !== 'product_flag') setSubject('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit ticket');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setSuccess(false);
    setError(null);
    onClose();
  };

  return (
    <div className="support-modal-overlay" onClick={handleClose}>
      <div className="support-modal" onClick={(e) => e.stopPropagation()}>
        <div className="support-modal-header">
          <h2 className="support-modal-title">{title}</h2>
          <button type="button" className="support-modal-close" onClick={handleClose}>
            <XMarkIcon className="support-modal-close-icon" />
          </button>
        </div>

        {success ? (
          <div className="support-modal-body">
            <p className="support-success-message">
              Your ticket has been submitted. Our team will review it and respond via email if needed.
            </p>
            <button type="button" className="support-submit-btn" onClick={handleClose}>
              Close
            </button>
          </div>
        ) : (
          <form className="support-modal-body" onSubmit={handleSubmit}>
            {product && (
              <p className="support-product-context">
                Product: <strong>{product.name}</strong>
              </p>
            )}

            <div className="support-form-group">
              <label htmlFor="support-subject" className="support-form-label">
                Subject *
              </label>
              <input
                id="support-subject"
                className="support-form-input"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                required
                placeholder={
                  type === 'product_flag'
                    ? 'Brief reason for reporting this product'
                    : 'How can we help?'
                }
              />
            </div>

            <div className="support-form-group">
              <label htmlFor="support-message" className="support-form-label">
                Message *
              </label>
              <textarea
                id="support-message"
                className="support-form-textarea"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
                rows={5}
                placeholder="Describe the issue in detail..."
              />
            </div>

            {error && <p className="support-error-message">{error}</p>}

            <div className="support-modal-actions">
              <button type="button" className="support-cancel-btn" onClick={handleClose}>
                Cancel
              </button>
              <button type="submit" className="support-submit-btn" disabled={submitting}>
                {submitting ? 'Submitting...' : 'Submit ticket'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default SupportTicketModal;
